import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Clock, Flag, Like1 } from "iconsax-reactjs";
import {
  Check,
  CircleChevronUp,
  MoreHorizontal,
  ChevronRight,
  X,
} from "lucide-react";
import {
  Avatar as TableAvatar,
  DataTableColumn,
} from "../../../shared/ui/DataTable";
import { Badge, BodyText, HeadingText } from "../../../shared/ui";
import {
  KybAssignmentStatus,
  KybComplianceRow,
  KybComplianceStatus,
  KybStatusBadgeTone,
} from "../types";
import { approvedActionColor } from "./kybShowReviewShared";

const mutedDocumentIconClassName = "text-[#C8CAE8]";

const renderDocumentMetric = (
  count: number,
  icon: ReactNode,
  iconClassName?: string,
) => (
  <div className="flex items-center gap-1.5">
    <span className="aku-body-small-regular text-[#2C2F44]">{count}</span>
    <span className={iconClassName}>{icon}</span>
  </div>
);

const ASSIGNMENT_STATUS_STYLES: Record<
  KybAssignmentStatus,
  { label: string; className: string }
> = {
  active: {
    label: "Active",
    className: "bg-[#DCFDDB] text-[#188B18]",
  },
  unassigned: {
    label: "Unassigned",
    className: "bg-[#FFE1E1] text-[#C01515]",
  },
  "needs-assignment": {
    label: "Needs assignment",
    className: "bg-[#FFEFC6] text-[#B64607]",
  },
};

const KYC_STATUS_TONE_STYLES: Record<KybStatusBadgeTone, string> = {
  success: "bg-[#DDF9D7] text-[#1D9B2C]",
  danger: "bg-[#FFE1E1] text-[#C01515]",
  warning: "bg-[#FDEECF] text-[#D96B00]",
  neutral: "bg-[#EEF1FB] text-[#6F739B]",
};

const renderStartedDocumentsMetric = (row: KybComplianceRow) => {
  const reviewedDocuments =
    row.pendingDocuments + row.approvedDocuments + row.rejectedDocuments;
  const totalDocuments =
    row.totalDocuments ??
    reviewedDocuments;

  return (
    <span className="aku-body-small-regular text-[#2C2F44]">
      {reviewedDocuments}/{totalDocuments}
    </span>
  );
};

const QueryStatusBadge = () => (
  <Badge className="h-6 bg-[#FFEFC6] px-2 aku-body-small-regular text-[#DB6704]">
    Queried
  </Badge>
);

const canApproveKybFromDocuments = (row: KybComplianceRow) => {
  const totalDocuments =
    row.totalDocuments ??
    row.pendingDocuments + row.approvedDocuments + row.rejectedDocuments;

  return totalDocuments > 0 && row.approvedDocuments === totalDocuments;
};

const renderAssignmentStatusBadge = (row: KybComplianceRow) => {
  if (row.kycAssignmentStatus) {
    const config = ASSIGNMENT_STATUS_STYLES[row.kycAssignmentStatus];

    return (
      <Badge className={`h-5 px-4 aku-body-small-regular ${config.className}`}>
        {config.label}
      </Badge>
    );
  }

  if (row.kycStatusLabel !== undefined || row.kycStatusTone) {
    const label = row.kycStatusLabel ?? "Null";
    const tone = row.kycStatusTone ?? "neutral";

    return (
      <Badge
        className={`h-5 px-4 aku-body-small-regular ${KYC_STATUS_TONE_STYLES[tone]}`}
      >
        {label}
      </Badge>
    );
  }

  const config = ASSIGNMENT_STATUS_STYLES.active;

  return (
    <Badge className={`h-5 px-4 aku-body-small-regular ${config.className}`}>
      {config.label}
    </Badge>
  );
};

const createNotStartedColumns = (): Array<DataTableColumn<KybComplianceRow>> => [
  {
    id: "customerName",
    header: "Customer name",
    cellClassName: "min-w-[200px]",
    cell: (row) => (
      <div className="flex items-center gap-4">
        <TableAvatar name={row.customerName} email={row.customerEmail} />
        <div className="flex min-w-0 items-center gap-2">
          <HeadingText className="truncate text-[14px] font-[500] text-[#161827]">
            {row.customerName}
          </HeadingText>
          {row.isQueried ? <QueryStatusBadge /> : null}
        </div>
      </div>
    ),
  },
  {
    id: "emailAddress",
    header: "Email address",
    cellClassName: "min-w-[180px] aku-body-small-regular text-[#383B56]",
    cell: (row) => row.customerEmail,
  },
  {
    id: "country",
    header: "Phone number",
    cellClassName: "min-w-[140px] aku-body-small-regular text-[#383B56]",
    cell: (row) => row.phoneNumber ?? "-",
  },
  {
    id: "registrationDate",
    header: "Registered",
    cellClassName:
      "min-w-[130px] whitespace-nowrap aku-body-small-regular text-[#383B56]",
    cell: (row) => row.registrationDate,
  },
];

type KybComplianceColumnActions = {
  onApproveKyc?: (row: KybComplianceRow) => void;
  onEscalate?: (row: KybComplianceRow) => void;
  onRejectKyc?: (row: KybComplianceRow) => void;
  onOpenDetails?: (row: KybComplianceRow) => void;
};

export const createKybComplianceColumns = (
  activeStatus: KybComplianceStatus,
  actions: KybComplianceColumnActions = {},
): Array<DataTableColumn<KybComplianceRow>> => {
  if (activeStatus === "not-started") {
    return createNotStartedColumns();
  }

  const columns: Array<DataTableColumn<KybComplianceRow>> = [
    {
      id: "customerName",
      header: "Customer name",
      cellClassName: "min-w-[200px]",
      cell: (row) => (
        <div className="flex items-center gap-4">
          <TableAvatar name={row.customerName} email={row.customerEmail} />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <HeadingText className="truncate text-[14px] font-[500] text-[#161827]">
                {row.customerName}
              </HeadingText>
              {row.isQueried ? <QueryStatusBadge /> : null}
            </div>
            <BodyText className="truncate text-[14px] font-[400] text-[#6F739B]">
              {row.customerEmail}
            </BodyText>
          </div>
        </div>
      ),
    },
    {
      id: "business",
      header: "Business",
      cellClassName: "min-w-[180px]",
      cell: (row) => (
        <div className="min-w-0">
          <HeadingText className="truncate text-[14px] font-[500] text-[#161827]">
            {row.businessName}
          </HeadingText>
          <BodyText className="truncate text-[14px] font-[400] text-[#6F739B]">
            {row.businessType || "-"}
          </BodyText>
        </div>
      ),
    },
  ];

  if (activeStatus === "approved") {
    columns.push({
      id: "kycStatus",
      header: "KYC Status",
      cellClassName: "min-w-[160px]",
      cell: (row) => renderAssignmentStatusBadge(row),
    });
  }

  columns.push({
    id: "documents",
    header: "Documents",
    cellClassName: "min-w-[150px]",
    cell: (row) =>
      activeStatus === "started" ? (
        renderStartedDocumentsMetric(row)
      ) : (
        <div className="flex items-center gap-6">
          {renderDocumentMetric(
            row.pendingDocuments,
            <Clock size={18} color="#C8CAE8" variant="Bold" />,
            row.pendingDocuments > 0
              ? "text-[#C8CAE8]"
              : mutedDocumentIconClassName,
          )}
          {renderDocumentMetric(
            row.approvedDocuments,
            <Like1
              size={18}
              color={
                row.approvedDocuments > 0
                  ? approvedActionColor
                  : "#C8CAE8"
              }
              variant="Bold"
            />,
            row.approvedDocuments > 0
              ? "text-[#1DCB1C]"
              : mutedDocumentIconClassName,
          )}
          {renderDocumentMetric(
            row.rejectedDocuments,
            <Flag
              size={18}
              color={row.rejectedDocuments > 0 ? "#F5A623" : "#C8CAE8"}
              variant="Bold"
            />,
            row.rejectedDocuments > 0
              ? "text-[#F5A623]"
              : mutedDocumentIconClassName,
          )}
        </div>
      ),
  });

  columns.push({
    id: "country",
    header: "Country",
    cellClassName: "min-w-[110px] aku-body-small-regular text-[#383B56]",
    cell: (row) => row.country,
  });

  columns.push({
    id: "registrationDate",
    header: "Registration date",
    cellClassName:
      "min-w-[130px] whitespace-nowrap aku-body-small-regular text-[#383B56]",
    cell: (row) => row.registrationDate,
  });

  columns.push({
    id: "actions",
    header: "",
    headerClassName: "w-[100px]",
    cellClassName: "min-w-[100px]",
    cell: (row) => {
      const canApprove = canApproveKybFromDocuments(row);
      const canShowActions =
        activeStatus !== "approved" && activeStatus !== "rejected";

      return (
        <div
          className="flex items-center justify-end gap-4"
          onClick={(event) => event.stopPropagation()}
        >
          {canShowActions ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F3F4FF]"
                  aria-label="Open row actions"
                >
                  <MoreHorizontal className="text-[#7A7FAA]" size={22} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-[120] min-w-[260px] rounded-[20px] border border-[#E1E6FF] bg-white p-3 shadow-[0_20px_45px_rgba(15,20,40,0.18)]"
                >
                  <DropdownMenu.Item
                    disabled={!canApprove}
                    onSelect={() => actions.onApproveKyc?.(row)}
                    className="flex cursor-pointer items-center gap-4 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                  >
                    <Check size={22} className="text-[#6F739B]" />
                    Approve KYC
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => actions.onEscalate?.(row)}
                    className="flex cursor-pointer items-center gap-4 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                  >
                    <CircleChevronUp size={22} className="text-[#6F739B]" />
                    Escalate to...
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => actions.onRejectKyc?.(row)}
                    className="flex cursor-pointer items-center gap-4 rounded-[14px] px-3 py-3 aku-body-normal-medium text-[#C01515] outline-none transition-colors focus:bg-[#FFF5F5]"
                  >
                    <X size={22} className="text-[#F24848]" />
                    Reject KYC
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null}
          <button
            type="button"
            onClick={() => actions.onOpenDetails?.(row)}
            className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F3F4FF]"
            aria-label="Open row details"
          >
            <ChevronRight className="text-[#7A7FAA]" size={22} />
          </button>
        </div>
      );
    },
  });

  return columns;
};
